import React, { useCallback, useContext, useEffect, useState, useRef } from "react";
import { Flex } from "rebass/styled-components";

import { layout } from "../../constants/constants";
import DynamicButton from "../../shared/components/dynamic-button/dynamic-button";
import ShIcon from "../../shared/components/sh-icon/sh-icon";
import { Media } from "../../shared/contexts/media/MediaContext";
import { UiStateContext } from "../../shared/contexts/ui-state/UiStateContext";
import useComponentVisible from "../../shared/hooks/component-visible/useComponentVisible";
import { FeatureToggle, IconPack, MegaReferenceLinkContent } from "../../shared/models/app-data-model";
import MegaMenuColumn from "../mega-menu-column/mega-menu-column";
import "./mega-menu.scss";

export type MegaMenuProps = {
  content: MegaReferenceLinkContent[];
  iconPack?: IconPack[];
  featureToggles: FeatureToggle[];
};

const MegaMenu: React.FC<MegaMenuProps> = ({ content, iconPack, featureToggles }: MegaMenuProps) => {
  const megaMenuContent = content?.[0];
  const uiStateContext = useContext(UiStateContext);
  const { ref, isComponentVisible } = useComponentVisible(true, "mm-nav-btn");

  const handleCloseButton = useCallback((): void => {
    uiStateContext.setMegaMenuOpen?.(false);
    uiStateContext.setMenuOpen?.(false);
  }, [uiStateContext.setMegaMenuOpen, uiStateContext.setMenuOpen]);

  const handleBackButton = useCallback((): void => {
    uiStateContext.setMegaMenuOpen?.(false);
    uiStateContext.setMenuOpen?.(true);
  }, [uiStateContext.setMegaMenuOpen, uiStateContext.setMenuOpen]);

  useEffect(() => {
    if (!isComponentVisible) {
      uiStateContext?.setMegaMenuOpen?.(false);
    }
  }, [isComponentVisible]);

  const mobileMegaMenuTop = (
    <Media lessThan="tablet">
      <Flex
        className="mm-mobile-top-section"
        alignItems="center"
        justifyContent={"space-between"}
        flexDirection="row"
        width={layout.pageWidth}
      >
        <button className="unstyled-btn mm-back-btn" data-testid="mm-back-btn" onClick={() => handleBackButton()}>
          <ShIcon iconPack={iconPack} className="icon back-arrow-icon" iconName="arrow" />
        </button>
        <button className="unstyled-btn mm-close-btn" data-testid="mm-close-btn" onClick={() => handleCloseButton()}>
          <ShIcon iconPack={iconPack} className="icon close-icon" iconName="close" />
        </button>
      </Flex>
    </Media>
  );

  const megaMenuJSX = (
    <>
      <Flex
        className="mega-menu-content"
        width={layout.pageWidth}
        px={layout.pagePaddingX}
        marginX={"auto"}
        data-testid="mega-menu-content"
      >
        <Media lessThan="desktop">
          <div className="mm-col-1-header-container">
            <div className="mm-col-1-header">{megaMenuContent?.column_1_content?.column_heading}</div>
          </div>
        </Media>

        <Media greaterThanOrEqual="desktop">
          <div className="mm-column-headings-container">
            <div className="mm-column-heading">{megaMenuContent?.column_1_content?.column_heading}</div>
            <div className="mm-column-heading">{megaMenuContent?.column_2_content?.column_heading}</div>
            <div className="mm-column-heading">{megaMenuContent?.column_3_content?.column_heading}</div>
            <div className="mm-column-heading">{megaMenuContent?.column_4_content?.column_heading}</div>
          </div>
        </Media>

        <div className="mm-columns-container">
          {megaMenuContent?.column_1_content && (
            <MegaMenuColumn
              content={megaMenuContent?.column_1_content}
              iconPack={iconPack}
              featureToggles={featureToggles}
              columnNumber={1}
            />
          )}
          {megaMenuContent?.column_2_content && (
            <MegaMenuColumn
              content={megaMenuContent?.column_2_content}
              iconPack={iconPack}
              featureToggles={featureToggles}
              columnNumber={2}
            />
          )}
          {megaMenuContent?.column_3_content && (
            <MegaMenuColumn
              content={megaMenuContent?.column_3_content}
              iconPack={iconPack}
              featureToggles={featureToggles}
              columnNumber={3}
            />
          )}
          {megaMenuContent?.column_4_content && (
            <MegaMenuColumn
              content={megaMenuContent?.column_4_content}
              iconPack={iconPack}
              featureToggles={featureToggles}
              columnNumber={4}
            />
          )}
        </div>
      </Flex>
      <Flex className="mm-menu-button-container" width={layout.pageWidth} marginX={"auto"} data-testid="mm-menu-button-container">
        {megaMenuContent?.menu_button && megaMenuContent?.menu_button?.reference && (
          <DynamicButton content={megaMenuContent?.menu_button} iconPack={iconPack} cssClasses="mm-menu-button" closeMenus={true} />
        )}
      </Flex>
    </>
  );

  return (
    <>
      <Media lessThan="desktop">
        <div className="mega-menu">
          {mobileMegaMenuTop}
          {megaMenuJSX}
        </div>
      </Media>

      <Media greaterThanOrEqual="desktop">
        <div className={`${isComponentVisible && "mega-menu"}`} ref={ref}>
          {isComponentVisible && megaMenuJSX}
        </div>
      </Media>
    </>
  );
};

export default MegaMenu;
